import React, { useState, useContext, useEffect } from 'react';

import { PageHeader, Row, Col } from 'antd';

import styled from 'styled-components';

import { saveAlerts } from '@libs/NwAlerts';

import { useStateValue } from '@contexts/StateProvider';
import { UserContext, useUserPermission } from '@contexts/UserContext';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { useAddContactTag, useRemoveContactTag } from '@hooks/API/Contact/UseContactTags';
import useConfigurations from '@hooks/Configurations/UseConfigurations';
import useLocalStorageState from '@hooks/Utils/UseLocalStorageState';

import AnagraphicalDrawer from '@components/ContactDetails/CommonComponents/Anagraphical/AnagraphicalDrawer';

import AlertDrawer from '@components/AlertDrawer/AlertDrawer';
import BillingInfoItemContactSummary from '@components/BillingInfos/BillingInfoItemContactSummary';
import ContactAlert from '@components/ContactAlert/ContactAlert';
import ContactAlertDrawer from '@components/ContactAlert/ContactAlertDrawer';
import ContactDetailsBankAccounts from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsBankAccounts';
import ContactDetailsHeaderAnagraphical from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsHeaderAnagraphical';
import ContactDetailsHeaderConnections from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsHeaderConnections';
import ContactHeaderBillingInfos from '@components/ContactDetails/ContactDetailsHeader/ContactHeaderBillingInfos';
import ContactDetailsNotes from '@components/ContactDetailsNotes/ContactDetailsNotes';
import NoteAddEditDrawer from '@components/ContactDetailsNotes/NoteAddEditDrawer';
import ClientAgencyFeeDrawer from '@components/ContactsCommissionsDrawers/ClientAgencyFeeDrawer';
import MotherAgencyCommissionDrawer from '@components/ContactsCommissionsDrawers/MotherAgencyCommissionDrawer';
import AddToFolderButton from '@components/ContactsFolders/AddToFolderButton';
import ContactUsers from '@components/ContactUsers/ContactUsers';
import CreatedBy from '@components/CreatedBy/CreatedBy';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import { NwAddButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import PermissionBlock from '@components/PermissionBlock/PermissionBlock';
import ObjectTagPicker from '@components/TagPicker/ObjectTagPicker';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const CustomerPageHeader = styled(PageHeader)`
    --ant-page-header-height: 17.5rem;
    --ant-page-header-width: 0;

    height: var(--ant-page-header-height) !important;

    .ant-page-header-heading {
        padding-left: 2rem !important;
    }

    .header-content {
        padding-left: 2rem !important;
        .header-body {
            padding-right: 3rem !important;

            .header-models-notes-container {
                border-left: ${(props) => props.theme.LighterBorder};
                padding-left: 1rem;
                padding-top: 0.15rem;

                .header-models-notes-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;

                    h2 {
                        font-size: ${(props) => props.theme.FontSizeSm};
                        text-transform: uppercase;
                        font-weight: 500;
                    }
                }

                .header-models-notes {
                    .header-models-note {
                        margin-bottom: 0.5rem;
                        font-size: ${(props) => props.theme.FontSizeSm};
                    }
                }
                .header-models-notes-more {
                    button {
                        span {
                            font-size: ${(props) => props.theme.FontSizeSm};
                        }
                    }
                }
            }
        }
    }
    .header-footer {
        margin-top: 1rem;
    }
    .ant-page-header-heading-title {
        width: 100%;
    }
    .ant-page-header-heading-left {
        width: 100%;
    }

    .info-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 0.5rem;
        line-height: 1.2em;
        margin-bottom: 0.15rem;
        font-size: ${(props) => props.theme.FontSizeSm};

        span {
            text-transform: uppercase;
            color: ${(props) => props.theme.TextColorMuted};
            min-width: 8rem;
        }
        strong {
            font-weight: 500;
            text-transform: uppercase;
            color: ${(props) => props.theme.TextColor};
        }
    }
`;

const CustomerDetailsHeaderDrawerAccounting = ({ currentContact, billingInfos, onReloadSearch }) => {
    const [{ areaContext }] = useStateValue();
    const { mutate: addTag } = useAddContactTag();
    const { mutate: removeTag } = useRemoveContactTag();

    const configurations = useConfigurations();

    const { currentUser } = useContext(UserContext);
    const [IsLoading, setIsLoading] = useState(true);
    const [showAlertDrawer, setShowAlertDrawer] = useState(false);
    const [readAlerts, setReadAlerts] = useLocalStorageState(`${areaContext.area}-read-alerts`, []);
    //const [isOpenHistory, setIsOpenHistory] = useState(false);
    const alertOnClients = useUserPermission(
        'AlertOnClients',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const alertOnAgencies = useUserPermission(
        'AlertOnAgencies',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const alertOnServices = useUserPermission(
        'AlertOnServices',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const tagsOnContacts = useUserPermission(
        'TagsOnContacts',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    let alertOnCustomers = false;
    if (currentContact?.SubType === 1) {
        alertOnCustomers = alertOnClients;
    }
    if (currentContact?.SubType === 2) {
        alertOnCustomers = alertOnAgencies;
    }
    if (currentContact?.SubType === 3) {
        alertOnCustomers = alertOnServices;
    }
    const [anagraphicalDrawer, setAnagraphicalDrawer] = useState(false);

    const [showCommissionDrawer, setShowCommissionDrawer] = useState(false);
    const [showNotesDrawer, setShowNotesDrawer] = useState(false);
    const [showNewNoteDrawer, setShowNewNoteDrawer] = useState(false);

    const handleOpenAnagraphicalDrawer = () => {
        setAnagraphicalDrawer(true);
    };

    const handleCustomerReload = () => {
        forceReloadContact(currentContact.ID);
    };

    useEffect(() => {
        if (currentContact) {
            setIsLoading(false);
        }
    }, [currentContact]);

    // const handleOpenHistory = () => {
    //     setIsOpenHistory(true);
    // };

    // const handleCloseHistory = () => {
    //     setIsOpenHistory(false);
    // };

    const onAddTag = (tag) => {
        addTag({ tag: tag, contactID: currentContact.ID });
    };

    const onRemoveTag = (tag) => {
        removeTag({ tag: tag, contactID: currentContact.ID });
    };

    const afterNotesCreation = () => {
        forceReloadContact(currentContact.ID);
        setShowNotesDrawer(false);
    };

    const afterNewNoteCreation = () => {
        forceReloadContact(currentContact.ID);
        setShowNewNoteDrawer(false);
    };

    const handleOpenAlertDrawer = () => {
        setShowAlertDrawer(true);
    };

    const handleCloseAlertDrawer = (updated) => {
        if (updated) {
            const alerts = readAlerts.filter(
                (alert) => alert.Author !== currentUser.UserID || alert.ContactID !== currentContact.ID
            );
            setReadAlerts(alerts);
            saveAlerts(alerts);
        }
        setShowAlertDrawer(false);
    };

    const getContactOrDefaultAgencyFee = () => {
        if (
            currentContact.NetworkAgencies[0].AgencyFee !== null &&
            currentContact.NetworkAgencies[0].AgencyFee !== undefined
        ) {
            return `${currentContact.NetworkAgencies[0].AgencyFee}%`;
        }
        return `${configurations.defaultAgencyFeePerc}% [default]`;
    };

    const getContactOrDefaultMotherAgencyCommission = () => {
        let stringCommission = '';
        if (
            currentContact.NetworkAgencies[0].MotherAgencyCommission !== null &&
            currentContact.NetworkAgencies[0].MotherAgencyCommission !== undefined
        ) {
            stringCommission = `${currentContact.NetworkAgencies[0].MotherAgencyCommission}%`;
            if (currentContact.NetworkAgencies[0].MotherAgencyCommissionOnGross) {
                stringCommission += ' on Gross';
            } else {
                stringCommission += ' on Net';
            }
            return stringCommission;
        }
        if (
            configurations.defaultMotherAgencyCommissionPerc !== null &&
            configurations.defaultMotherAgencyCommissionPerc !== undefined
        ) {
            stringCommission += `${configurations.defaultMotherAgencyCommissionPerc}%`;
            if (configurations.defaultMotherAgencyCommissionPercOnGross) {
                stringCommission += ' on Gross';
            }
            stringCommission += ' [default]';
            return stringCommission;
        }
        return '0%';
    };

    return currentContact ? (
        IsLoading ? (
            <Loading />
        ) : (
            <>
                <ContactAlertDrawer contactId={currentContact.ID} />
                <div className="details-header-container details-header-container-drawer">
                    <CustomerPageHeader
                        onBack={null}
                        title={<FlexContainer wrap="wrap" justifyContent="space-between" fullWidth></FlexContainer>}
                        subTitle={false}
                    >
                        <div className="header-content">
                            <div className="header-body">
                                <Row gutter={15}>
                                    <Col span={14}>
                                        <div className="header-body-name-container">
                                            <div className="name-wrapper">
                                                <h1 onClick={handleOpenAnagraphicalDrawer}>
                                                    {currentContact.Name.toLowerCase()}
                                                </h1>
                                                {anagraphicalDrawer && (
                                                    <AnagraphicalDrawer
                                                        currentContact={currentContact}
                                                        type={0}
                                                        onClose={() => {
                                                            setAnagraphicalDrawer(false);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <span className="name-icon">
                                                <AddToFolderButton
                                                    ids={[currentContact.ID]}
                                                    type="customer"
                                                    size="lg"
                                                    showBadge
                                                />
                                            </span>
                                            <span className="name-icon">
                                                {alertOnCustomers ? (
                                                    <NwIcon
                                                        icon={byPrefixAndName.fasl['comment-alt-exclamation']}
                                                        plus={!currentContact.Alert}
                                                        edit={currentContact.Alert}
                                                        pointer
                                                        onClick={handleOpenAlertDrawer}
                                                    />
                                                ) : (
                                                    <ContactAlert
                                                        currentContact={currentContact}
                                                        readOnly
                                                        icon={
                                                            <NwIcon
                                                                size="lg"
                                                                icon={byPrefixAndName.fasl['comment-alt-exclamation']}
                                                                pointer
                                                            />
                                                        }
                                                    />
                                                )}
                                            </span>
                                            <span className="name-icon">
                                                <PermissionBlock
                                                    permission={tagsOnContacts}
                                                    content={
                                                        <ObjectTagPicker
                                                            readOnly={!tagsOnContacts}
                                                            itemType={currentContact.ContactType}
                                                            tags={currentContact.Tags}
                                                            onAddTag={tagsOnContacts && onAddTag}
                                                            onRemoveTag={tagsOnContacts && onRemoveTag}
                                                            stripMaxWidth="14rem"
                                                        />
                                                    }
                                                />
                                            </span>
                                        </div>
                                        <Row gutter={15}>
                                            <Col span={12}>
                                                <div className="header-body-boxes-container">
                                                    <div className="header-body-boxes header-body-boxes-hoverable">
                                                        <ContactUsers
                                                            users={currentContact.Users}
                                                            contactID={currentContact.ID}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="header-body-boxes-container model-info-line">
                                                    <ContactDetailsHeaderAnagraphical contact={currentContact} />
                                                </div>
                                                {/* <ContactDetailsHeaderConnections contact={currentContact} /> */}
                                            </Col>
                                            <Col span={12}>
                                                {currentContact.NetworkAgencies &&
                                                    currentContact.NetworkAgencies.length > 0 &&
                                                    currentContact.SubType === 1 && (
                                                        <div className="info-row">
                                                            <span>Agency Fee</span>
                                                            <strong>{getContactOrDefaultAgencyFee()}</strong>
                                                            <NwIcon
                                                                icon={byPrefixAndName.fasl['edit']}
                                                                primary
                                                                pointer
                                                                style={{ marginLeft: '.5rem' }}
                                                                onClick={() => setShowCommissionDrawer(true)}
                                                            />
                                                        </div>
                                                    )}
                                                {currentContact.NetworkAgencies &&
                                                    currentContact.NetworkAgencies.length > 0 &&
                                                    currentContact.SubType === 2 && (
                                                        <div className="info-row">
                                                            <span>Mother Agency Commission</span>
                                                            <strong>
                                                                {getContactOrDefaultMotherAgencyCommission()}
                                                            </strong>
                                                            <NwIcon
                                                                icon={byPrefixAndName.fasl['edit']}
                                                                primary
                                                                pointer
                                                                style={{ marginLeft: '.5rem' }}
                                                                onClick={() => setShowCommissionDrawer(true)}
                                                            />
                                                        </div>
                                                    )}

                                                {billingInfos && billingInfos.length > 0 && (
                                                    <BillingInfoItemContactSummary
                                                        item={billingInfos[0]}
                                                        currentContact={currentContact}
                                                    />
                                                )}
                                                <div
                                                    className="header-body-boxes-container"
                                                    style={{ marginTop: '.5rem' }}
                                                >
                                                    <ContactHeaderBillingInfos currentContact={currentContact} />
                                                </div>
                                                <div
                                                    className="header-body-boxes-container"
                                                    style={{ marginTop: '.5rem' }}
                                                >
                                                    <ContactDetailsBankAccounts currentContact={currentContact} />
                                                </div>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col span={10}>
                                        <div className="header-models-notes-container">
                                            <div className="header-models-notes-header">
                                                <h2>Billing Notes</h2>
                                                <NwIcon
                                                    pointer
                                                    icon={byPrefixAndName.fasl['sidebar-flip']}
                                                    onClick={() => setShowNotesDrawer(true)}
                                                />
                                            </div>
                                            {currentContact.Notes &&
                                                currentContact.Notes.length > 0 &&
                                                currentContact.Notes.filter((x) => x.NoteTypeCode === 'BILLING')
                                                    .length > 0 && (
                                                    <div className="header-models-notes">
                                                        {currentContact.Notes.filter(
                                                            (x) => x.NoteTypeCode === 'BILLING'
                                                        )
                                                            .slice(0, 2)
                                                            .map((x) => (
                                                                <div className="header-models-note" key={x.ID}>
                                                                    <NwEllipsis text={x.Text} popover rows={2} />
                                                                    <CreatedBy object={x} />
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        <div className="header-footer">
                            <ContactDetailsHeaderConnections inFooter contact={currentContact} />
                        </div>
                    </CustomerPageHeader>
                </div>
                {showAlertDrawer && <AlertDrawer currentContact={currentContact} onClose={handleCloseAlertDrawer} />}
                {showCommissionDrawer &&
                    (currentContact.SubType === 1 ? (
                        <ClientAgencyFeeDrawer
                            networkAgency={currentContact.NetworkAgencies[0]}
                            customer={currentContact}
                            onSetCustomer={handleCustomerReload}
                            onClose={() => setShowCommissionDrawer(false)}
                            onReloadSearch={onReloadSearch}
                        />
                    ) : (
                        <MotherAgencyCommissionDrawer
                            networkAgency={currentContact.NetworkAgencies[0]}
                            customer={currentContact}
                            onSetCustomer={handleCustomerReload}
                            onClose={() => setShowCommissionDrawer(false)}
                            onReloadSearch={onReloadSearch}
                        />
                    ))}
                {showNotesDrawer && (
                    <NwDrawer
                        title="Billing Notes"
                        onClose={() => setShowNotesDrawer(false)}
                        extra={<NwAddButton ghost onClick={() => setShowNewNoteDrawer(true)} />}
                    >
                        <ContactDetailsNotes
                            currentContact={currentContact}
                            isInCardTab
                            drawerMode
                            afterNotesCreation={afterNotesCreation}
                            noEllipsis
                            noPagination
                            type="billing"
                        />
                    </NwDrawer>
                )}
                {showNewNoteDrawer && (
                    <NwDrawer title="New Billing Note" onClose={() => setShowNewNoteDrawer(false)}>
                        <NoteAddEditDrawer
                            contactID={currentContact.ID}
                            billingNote
                            afterNotesCreation={afterNewNoteCreation}
                        />
                    </NwDrawer>
                )}
                {/* {isOpenHistory && (
                    <NwDrawer title="Model Logs" onClose={handleCloseHistory}>
                        <ObjectLogHistories objectId={currentContact.ID} type="model" />
                    </NwDrawer>
                )} */}
            </>
        )
    ) : (
        <Loading />
    );
};

export default CustomerDetailsHeaderDrawerAccounting;
